import type { Daten } from '../daten/laden.js';
import { SKALA_MODUS } from '../daten/skala.js';

export function Hinweise({ daten }: { daten: Daten }) {
  const m = daten.meta;
  const nichtAusgewertet = Object.values(daten.fassungen.gebiete).flat().filter((f) => !f.ausgewertet);
  const fehlendeAuswertungen = {
    gekappt: nichtAusgewertet.filter((f) => f.pdf_status === 'gekappt').length,
    fehlt: nichtAusgewertet.filter((f) => f.pdf_status === 'fehlt').length,
    vorhanden: nichtAusgewertet.filter((f) => f.pdf_status === 'ok').length,
    unbekannt: nichtAusgewertet.filter((f) => f.pdf_status === 'unbekannt').length,
    uebernommen: nichtAusgewertet.filter((f) => f.konzessionaer_herkunft === 'uebernommen').length,
  };
  const exportiert = new Date(m.exportiert_am).toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    dateStyle: 'long',
    timeStyle: 'short',
  });
  return (
    <article className="seite">
      <h1>Daten und Methodik</h1>

      <h2>Woher die Daten stammen</h2>
      <p>
        Grundlage sind die Technischen Anschlussbedingungen (TAB) der Feuerwehren und Leitstellen für die Aufschaltung von
        Brandmeldeanlagen, wie sie auf din-14675.de und in Webarchiven seit 2004 dokumentiert sind. Erfasst sind{' '}
        {m.anzahl['gebiete']} Gebiete und {m.anzahl['fassungen']} TAB-Fassungen; davon wurden{' '}
        {m.anzahl['fassungen_ausgewertet']} inhaltlich ausgewertet.
      </p>

      {nichtAusgewertet.length > 0 && (
        <section aria-labelledby="h-auswertungsluecken">
          <h2 id="h-auswertungsluecken">Warum nicht alle Fassungen ausgewertet sind</h2>
          <p>Für {nichtAusgewertet.length} erfasste TAB-Fassungen liegt keine inhaltliche Auswertung vor:</p>
          <ul>
            {fehlendeAuswertungen.gekappt > 0 && (
              <li>{fehlendeAuswertungen.gekappt} PDF-Dateien sind nur gekappt im Bestand vorhanden.</li>
            )}
            {fehlendeAuswertungen.fehlt > 0 && (
              <li>{fehlendeAuswertungen.fehlt} PDF-Dateien fehlen im Bestand.</li>
            )}
            {fehlendeAuswertungen.vorhanden > 0 && (
              <li>{fehlendeAuswertungen.vorhanden} PDF-Dateien sind als vollständig erfasst; ihre Auswertung steht noch aus.</li>
            )}
            {fehlendeAuswertungen.unbekannt > 0 && (
              <li>Bei {fehlendeAuswertungen.unbekannt} Fassungen ist der Zustand der PDF-Datei unbekannt.</li>
            )}
          </ul>
          <p>
            Bei {fehlendeAuswertungen.uebernommen} dieser Fassungen wurde der Konzessionär aus einer älteren Fassung
            übernommen. Das ist im Zeitstrahl und in der Fassungstabelle gekennzeichnet und ersetzt keine Auswertung
            der neueren Fassung. Bei den übrigen {nichtAusgewertet.length - fehlendeAuswertungen.uebernommen} Fassungen
            bleibt der Konzessionär offen.
          </p>
        </section>
      )}

      <h2>Wie der Konzessionär bestimmt wird</h2>
      <ul>
        <li>Der Konzessionär wird maschinell aus dem Text der jeweiligen TAB-Fassung ermittelt und nicht abschließend geprüft.</li>
        <li>Eine Fassung gilt von ihrem Standdatum bis zum Standdatum der nächsten bekannten Fassung.</li>
        <li>
          Fehlt das Standdatum, gilt das Datum des ersten Nachweises im Webarchiv; die Fassung ist dann als „spätestens“
          gekennzeichnet.
        </li>
        <li>
          Liegt für eine Fassung keine Auswertung vor, wird ein zuvor ermittelter Konzessionär aus einer älteren Fassung
          übernommen und gekennzeichnet, sofern ein solcher bekannt ist.
        </li>
        <li>Nach der letzten bekannten Fassung ist offen, ob sie fortgilt; die Darstellung schreibt sie fort.</li>
      </ul>

      {SKALA_MODUS !== 'aus' && (
        <>
          <h2>Vergleichbarkeit mit Berlin</h2>
          <p>{daten.skala.beschreibung}</p>
          <p>
            Grundlage ist ein Abgleich der Leistungsmerkmale, die die TAB dem Konzessionär zuweisen, mit den Berliner
            Anschlussbedingungen. „Vorläufig“ heißt: Der Abgleich ist für dieses Gebiet begonnen, aber noch nicht
            abgeschlossen.
          </p>
        </>
      )}

      <h2>Karte</h2>
      <p>
        Kartengrundlage © OpenStreetMap-Mitwirkende (Lizenz ODbL). Die Punkte markieren den Sitz des jeweiligen Gebiets,
        nicht seine Grenzen.
      </p>

      <h2>Datenstand</h2>
      <p className="hinweis-klein">
        Export vom {exportiert} Uhr · Quelle {m.quelle.datei} (SHA-256 {m.quelle.sha256.slice(0, 12)}…) · Exportskript
        Version {m.skript_version}
      </p>
    </article>
  );
}
